import { takeLatest, call, put, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import { types, selectors, actions } from "reducers/recoveryPassword";
import { actions as loginActions } from "reducers/login";
import { actions as notificationActions } from "reducers/notification";
import { adjustIdFieldErrors } from "util/form.js";
import gaEventTracker, { events } from "util/gaEventTracker";
import * as recovery from "middleware/recoveryPassword";

const sagas = [
    takeLatest(types.RECOVERY_PASS_BACK_TO_STEP_1, handleGoToStep1),
    takeLatest(types.RECOVERY_PASS_STEP1_REQUEST, handleRecoveryPassStep1),
    takeLatest(types.RECOVERY_PASS_STEP2_REQUEST, handleRecoveryPassStep2),
    takeLatest(types.RECOVERY_PASS_STEP3_REQUEST, handleRecoveryPassStep3),
    takeLatest(types.RESET_EXPIRED_PASS_PRE_REQUEST, handleResetExpiredPassPreRequest),
    takeLatest(types.RESET_EXPIRED_PASS_REQUEST, handleResetExpiredPassRequest),
];

export default sagas;

function* handleGoToStep1({ recoveryType }) {
    yield put(push(`/recovery/${recoveryType}/step1`));
}

function* handleRecoveryPassStep1({ username, secondFactor, recaptchaResponse, formikBag }) {
    const recoveryType = formikBag?.props?.match?.params?.recoveryType;
    const response = yield call(recovery.recoveryPassStep1, username, secondFactor, recaptchaResponse, recoveryType);

    if (response.type === "W") {
        formikBag.setErrors(adjustIdFieldErrors(response.data.data));

        if (response.data.code === "API021W" || response.data.code === "COR050W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
            yield put({ type: types.RECOVERY_PASS_FAILURE_REQUIRE_CAPTCHA });
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
        }
    } else {
        yield put(push(`/recovery/${recoveryType}/step2`));
    }

    formikBag.setSubmitting(false);
}

function* handleRecoveryPassStep2({ code, formikBag }) {
    const recoveryType = formikBag?.props?.match?.params?.recoveryType;
    const response = yield call(recovery.recoveryPassStep2, code, recoveryType);

    if (response.type === "W") {
        formikBag.setErrors(adjustIdFieldErrors(response.data.data));
        if (response.data.code === "COR020W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
        } else {
            // exchangeToken expired, restart flow
            yield put({ type: types.CLEAN });
            yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
            yield put(push(`/recovery/${recoveryType}/step2`));
        }
    } else {
        const { _exchangeToken, _resetCode } = response.data.data;

        yield put({ type: types.RECOVERY_PASS_STEP2_SUCCESS, exchangeToken: _exchangeToken, resetCode: _resetCode });
        yield put(push(`/recovery/${recoveryType}/step3`));
    }
    formikBag.setSubmitting(false);
}

function* handleRecoveryPassStep3({
    newPassword,
    newPasswordConfirmation,
    resetCode,
    exchangeToken,
    formikBag,
    recoveryType,
}) {
    try {
        const response = yield call(
            recovery.recoveryPassStep3,
            newPassword,
            newPasswordConfirmation,
            resetCode,
            exchangeToken,
            recoveryType,
        );
        if (response.type === "W") {
            formikBag.setErrors(adjustIdFieldErrors(response.data.data));
            if (response.data.code === "COR020W") {
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
            } else {
                // exchangeToken expired, restart flow
                yield put({ type: types.CLEAN });
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put(push(`/recovery/${recoveryType}/step2`));
            }
        } else {
            gaEventTracker.triggerEvent(events.passRecovery);
            yield put({ type: types.RECOVERY_PASS_SUCCESS });

            yield put(push(`/recovery/${recoveryType}/step4`));
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error?.response?.data?.message, "error", ["recoveryPassword"]));
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* handleResetExpiredPassPreRequest({ exchangeToken }) {
    yield put(actions.resetExpiredPasswordPreSuccess(exchangeToken));
    yield put(push("/resetExpiredPasswordStep1"));
}

function* handleResetExpiredPassRequest({ password, newPassword, passwordConfirmation, formikBag }) {
    const exchangeToken = yield select(selectors.getExchangeToken);
    const response = yield call(recovery.resetPassword, password, newPassword, passwordConfirmation, exchangeToken);
    if (response.type === "W") {
        formikBag.setErrors(adjustIdFieldErrors(response.data.data));
        yield put(actions.resetExpiredPassError());
    } else {
        const { _userName } = response.data.data;

        yield put(
            loginActions.setRememberedUser({
                username: _userName,
                userFullName: "",
            }),
        );
        yield put(actions.resetExpiredPassSuccess());
        yield put(push("/"));
        yield put(
            notificationActions.showNotification(
                response?.data?.message || "Password recuperado correctamente",
                "success",
                ["login"],
            ),
        );
    }
    formikBag.setSubmitting(false);
}
